import React, { useState, useMemo, useEffect, useRef } from "react";
import SwipeableViews, { SwipeableViewsProps } from "react-swipeable-views";
import { autoPlay, WithAutoPlayProps } from "react-swipeable-views-utils";
import { IStyleProps, NeverT } from "@/ui-component/interface";
import classnames from "classnames";
import CustomPagination from "./CustomPagination";
import styles from "./customCarousel.module.scss";

interface IClasses {
    container: string;
    dots: string;
}

interface IAutoPlay {
    autoplay?: boolean;
}

type AutoPlay = (IAutoPlay & WithAutoPlayProps) | NeverT<keyof IAutoPlay>;
interface ICustomCarouselProps extends IStyleProps, SwipeableViewsProps {
    dots?: boolean; // 是否显示分页指示器
    infinite?: boolean; // 无限循环播放
    contentList?: any[]; // 内容列表
    carouselClasses?: Partial<IClasses>;
}

type IProps = ICustomCarouselProps & AutoPlay;

const defaultProps = {
    dots: false,
    infinite: false
};

const CustomSwipeableViews: React.FC<IProps> = (prop) => {
    const { infinite, onChangeIndex, index, carouselClasses, contentList = [], ...props } =
        useMemo(() => {
            return { ...prop };
        }, [prop]);
    const [currentIndex, setCurrentIndex] = useState(index ?? 0);
    const [isVisible, setIsVisible] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const childrenCount = React.Children.count(props.children);

    // 检测是否存在视频内容
    const hasVideoContent = contentList.some(
        (item: any) => typeof item === 'object' && item.video_url
    );

    // 实际是否自动播放（无视频且开启自动播放）
    const shouldAutoplay = props.autoplay && !hasVideoContent;

    // IntersectionObserver 检测可视状态
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsVisible(entry.isIntersecting);
            },
            { threshold: 0.8 } // 50%可见时触发
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            if (containerRef.current) {
                observer.unobserve(containerRef.current);
            }
        };
    }, []);

    // 自动播放逻辑（仅在可视区域且允许自动播放时运行）
    useEffect(() => {
        let timer: NodeJS.Timeout;
        
        if (shouldAutoplay && isVisible) {
            timer = setInterval(() => {
                setCurrentIndex(prev => {
                    const nextIndex = infinite 
                        ? (prev + 1) % childrenCount 
                        : Math.min(prev + 1, childrenCount - 1);
                    
                    onChangeIndex?.(nextIndex, childrenCount);
                    return nextIndex;
                });
            }, 3000);
        }

        return () => {
            if (timer) clearInterval(timer);
        };
    }, [shouldAutoplay, isVisible, infinite, childrenCount]);

    // 同步外部index变化
    useEffect(() => {
        setCurrentIndex(index ?? 0);
    }, [index]);

    const handleChangeIndex = (index: number) => {
        setCurrentIndex(index);
        onChangeIndex?.(index, childrenCount);
    };
    
    return (
        <div
            ref={containerRef}
            style={props.style}
            className={classnames(
                styles.carouselContainer,
                "carousel-container",
                props.className,
            )}
        >
            <SwipeableViews
                index={currentIndex}
                onChangeIndex={handleChangeIndex}
                className={carouselClasses?.container}
                slideStyle={props.slideStyle}
                onSwitching={props.onSwitching}
                onDrag={props.onDrag}
            >
                {props.children}
            </SwipeableViews>
            <CustomPagination
                dots={childrenCount}
                index={currentIndex}
                className={classnames(
                    {
                        [styles.hidden]: !props.dots,
                    },
                    carouselClasses?.dots,
                )}
                onChangeIndex={handleChangeIndex}
            />
        </div>
    );
};


export default CustomSwipeableViews;
